/**
 * Search form component for Scheduled Appointments
 * Supports search by Patient Name, Appointment Date, Clinic Name, and User Name
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Search, X, CalendarIcon } from 'lucide-react';
import { format, parse } from 'date-fns';
import { cn } from '@/lib/utils';

export interface AppointmentSearchFilters {
  patientName: string;
  appointmentDate: string;
  clinicName: string;
  userName: string;
}

interface ScheduledAppointmentsSearchFormProps {
  onSearch: (filters: AppointmentSearchFilters) => void;
  onReset: () => void;
  isLoading?: boolean;
  type: 'virtual' | 'in-person';
  currentFilters?: { patientName?: string; appointmentDate?: string; clinicName?: string; userName?: string };
}

export const ScheduledAppointmentsSearchForm: React.FC<ScheduledAppointmentsSearchFormProps> = ({
  onSearch,
  onReset,
  isLoading = false,
  type,
  currentFilters = {},
}) => {
  const [patientName, setPatientName] = useState(currentFilters.patientName || '');
  const [appointmentDate, setAppointmentDate] = useState<Date | undefined>(
    currentFilters.appointmentDate ? parse(currentFilters.appointmentDate, 'yyyy-MM-dd', new Date()) : undefined
  );
  const [clinicName, setClinicName] = useState(currentFilters.clinicName || '');
  const [userName, setUserName] = useState(currentFilters.userName || '');
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Sync local state with external filters when they change
  useEffect(() => {
    setPatientName(currentFilters.patientName || '');
    setAppointmentDate(
      currentFilters.appointmentDate ? parse(currentFilters.appointmentDate, 'yyyy-MM-dd', new Date()) : undefined
    );
    setClinicName(currentFilters.clinicName || '');
    setUserName(currentFilters.userName || '');
  }, [currentFilters.patientName, currentFilters.appointmentDate, currentFilters.clinicName, currentFilters.userName]);

  const handleSearch = () => {
    onSearch({
      patientName: patientName.trim(),
      appointmentDate: appointmentDate ? format(appointmentDate, 'yyyy-MM-dd') : '',
      clinicName: clinicName.trim(),
      userName: userName.trim(),
    });
  };

  const handleReset = () => {
    setPatientName('');
    setAppointmentDate(undefined);
    setClinicName('');
    setUserName('');
    onReset();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const hasFilters = patientName || appointmentDate || clinicName || userName;

  return (
    <div id={`scheduled_appointments_search_form_${type}`} className="bg-card border border-border rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Patient Name */}
        <div className="space-y-2">
          <Label htmlFor={`patientName_${type}`} className="text-sm font-medium text-foreground">
            Patient Name
          </Label>
          <Input
            id={`patientName_${type}`}
            placeholder="Search by patient name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-10"
          />
        </div>

        {/* Appointment Date */}
        <div className="space-y-2">
          <Label htmlFor={`appointmentDate_${type}`} className="text-sm font-medium text-foreground">
            Appointment Date
          </Label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                id={`appointment_date_picker_${type}_btn`}
                variant="outline"
                className={cn(
                  'w-full h-10 justify-start text-left font-normal',
                  !appointmentDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {appointmentDate ? format(appointmentDate, 'MMM dd, yyyy') : 'Select date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent id={`appointment_date_popover_${type}`} className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={appointmentDate}
                onSelect={(date) => {
                  setAppointmentDate(date);
                  setDatePickerOpen(false);
                }}
                initialFocus
                className={cn('p-3 pointer-events-auto')}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Clinic Name */}
        <div className="space-y-2">
          <Label htmlFor={`clinicName_${type}`} className="text-sm font-medium text-foreground">
            Clinic Name
          </Label>
          <Input
            id={`clinicName_${type}`}
            placeholder="Search by clinic name"
            value={clinicName}
            onChange={(e) => setClinicName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-10"
          />
        </div>

        {/* User Name / Facility Name */}
        <div className="space-y-2">
          <Label htmlFor={`userName_${type}`} className="text-sm font-medium text-foreground">
            {type === 'virtual' ? 'User Name' : 'Facility Name'}
          </Label>
          <Input
            id={`userName_${type}`}
            placeholder={type === 'virtual' ? 'Search by user name' : 'Search by facility name'}
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-10"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <Button
          id={`scheduled_appointments_search_${type}_btn`}
          onClick={handleSearch}
          disabled={isLoading}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Search className="h-4 w-4 mr-2" />
          Search
        </Button>
        {hasFilters && (
          <Button
            id={`scheduled_appointments_reset_${type}_btn`}
            onClick={handleReset}
            variant="outline"
            disabled={isLoading}
            className="border-border"
          >
            <X className="h-4 w-4 mr-2" />
            Reset
          </Button>
        )}
      </div>
    </div>
  );
};
